import { ENDORSEMENT_REASONS, ENDORSEMENT_TYPES } from '@domain/endorsements/constants';
import { QueryFiltersSchema } from '@domain/schemas/filters.schema';
import { TagSchema } from '@domain/schemas/tag.schema';
import { EVAL_CONFIG_STARTERS, type EvalConfigStarterId } from '@root/helpers/constants';
import dayjs from 'dayjs';
import { z } from 'zod';

import {
  EvalConfigDraftContentSchema,
  EvalConfigRootSchema,
  EvalModelIdSchema,
  EvalWorkflowTypeSchema,
} from './evals/eval-config.schema';
import { EvalFindingGradeSchema, EvalFindingResolutionSchema } from './evals/eval-run.schema';
import { ApiPatchOperation } from './api-patch-operation';
import { LibraryBlockSchema, SubmissionBlockSchema, TemplateBlockSchema } from './block.schema';
import { DbCommentSchema } from './comment.schema';
import { DbContractSchema, DbEndorsementSchema, DbSubmissionStatusSchema, ResourceState } from './contract.schema';
import { EndorsementPremiumChange } from './endorsement-premium-change';
import { IdentityProvider } from './identity-provider';
import { SAVED_VIEW_NAME_MAX_LENGTH } from './saved-view.schema';
import { SectionSchema } from './section.schema';
import { DbTeamSchema, TeamSchema } from './team.schema';
import { dbUserSchema } from './user.schema';

export const resourceIdSchema = z.string().uuid();
export const rootResourceNameSchema = z.enum([
  'submissions',
  'templates',
  'brandings',
  'blocks',
  'images',
  'tags',
  'users',
  'teams',
  'endorsements',
  'endorsement-summary-templates',
  'saved-views',
  'search',
  'eval_configs',
  'eval_runs',
  'feature_access',
]);

export const createContractPayloadSchema = DbContractSchema.pick({
  branding_id: true,
  branding: true,
  class_of_business: true,
  name: true,
  teams: true,
});

export const updateEndorsementPayloadSchema = DbEndorsementSchema.pick({
  additional_premium: true,
  premium_change: true,
  effective_date: true,
  expiry_date: true,
  reason: true,
  reference: true,
  type: true,
  internal_description: true,
  status: true,
})
  .partial()
  .extend({
    operation: z.literal(ApiPatchOperation.UpdateResourceData),
    shouldBypassDatapointValidation: z.boolean().optional(),
  });

export const updateSubmissionsPayloadSchema = DbContractSchema.pick({
  status: true,
  branding_id: true,
  branding: true,
  class_of_business: true,
  name: true,
  tags: true,
  teams: true,
})
  .partial()
  .extend({
    operation: z.literal(ApiPatchOperation.UpdateResourceData),
    shouldBypassDatapointValidation: z.boolean().optional(),
  });

export const syncSubmissionDataPayloadSchema = z.object({
  operation: z.literal(ApiPatchOperation.SyncSubmissionData),
  keepLocal: z.boolean().optional(),
});

export const updateResourceDataItemsPayloadSchema = DbContractSchema.pick({
  data_items: true,
}).extend({
  operation: z.literal(ApiPatchOperation.UpdateResourceDataItems),
});

export const changeSubmissionBaseTemplatePayloadSchema = z.object({
  operation: z.literal(ApiPatchOperation.ChangeBaseTemplate),
  /**
   * Reference to new template that is going to be used. The new base template for a submission (contract).
   */
  sourceResourceId: z.string().uuid(),
});

export const preDraftToDraftPayloadSchema = DbContractSchema.pick({
  branding_id: true,
  branding: true,
  class_of_business: true,
  name: true,
  teams: true,
})
  .partial()
  .extend({
    operation: z.literal(ApiPatchOperation.PreDraftToDraft),
    copy_from_submission: z.string().uuid().optional(),
    copy_from_template: z.string().uuid().optional(),
  })
  .refine(
    (data) =>
      (data.copy_from_template && !data.copy_from_submission) ||
      (!data.copy_from_template && data.copy_from_submission),
    {
      message: 'Either copy_from_template or copy_from_submission must be present, but not both.',
      path: ['copy_from_template', 'copy_from_submission'],
    },
  );

export const updateTemplatePayloadSchema = updateSubmissionsPayloadSchema.extend({
  teams: z.array(z.union([z.string(), z.number()])).optional(),
});

export const createTemplatePayloadSchema = createContractPayloadSchema.extend({
  copy_from_template: z.string(),
});

export const createRenewalSubmissionPayloadSchema = z.object({
  name: z.string(),
  baseTemplateId: z.string().optional(),
  keepPreviouslyAddedBlocks: z.boolean().default(false).optional(),
  skipPreviouslyRemovedBlocks: z.boolean().default(false).optional(),
});

export const createRenewalSubmissionSpaPayloadSchema = createRenewalSubmissionPayloadSchema.extend({
  mergeEndorsements: z
    .object({
      endorsementIds: z.array(z.string()),
      order: z.enum(['effective', 'finalised']),
    })
    .optional(),
});

// External (M2M) equivalent of `mergeEndorsements` above — same behaviour, snake_case naming
// to match the rest of the Pro API's request shape.
export const createRenewalSubmissionProApiPayloadSchema = createRenewalSubmissionPayloadSchema.extend({
  merge_endorsements: z.array(z.string()).optional(),
  order: z.enum(['effective_from', 'date_finalised']).default('effective_from'),
});

export const createRenewalSubmissionResponseSchema = z
  .object({
    id: z.string().uuid(),
    movedBlockIds: z.array(z.string().uuid()),
  })
  .strict();

export const createSubmissionPayloadSchema = createContractPayloadSchema
  .extend({
    copy_from_template: z.string().optional(),
    copy_from_submission: z.string().optional(),
  })
  .refine(
    (data) =>
      (data.copy_from_template && !data.copy_from_submission) ||
      (!data.copy_from_template && data.copy_from_submission),
    {
      message: 'Either copy_from_template or copy_from_submission must be present, but not both.',
      path: ['copy_from_template', 'copy_from_submission'],
    },
  );

// Skeleton payload from NextGen to create a new submission
export const createEmptySubmissionPayloadSchema = z.object({
  contractId: z.string().uuid(),
  contractDescription: z.string(),
  teamId: z.string().uuid(),
  lineOfBusiness: z.string().optional(),
});

/**
 * NextGen payload to check if a submission is ready
 */
export const submissionSyncCheckPayloadSchema = z
  .object({
    syncType: z.enum(['Sync', 'FOReady', 'Both']),
    hash: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.syncType === 'FOReady') {
      return;
    }

    if (!data.hash) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Hash is required when syncType is not FOReady',
        path: ['hash'],
      });
    }
  });

/**
 * NextGen payload to reassign a team to a submission
 */
export const pplSubmissionReassignTeamPayloadSchema = z.object({
  broker_team: z.object({
    team_id: z.string().uuid(),
  }),
});

export const createContractSectionPayloadSchema = SectionSchema.omit({
  parentSectionId: true, // deprecated
}).extend({
  operation: z.literal(ApiPatchOperation.CreateResourceSection),
});

export const updateContractSectionPayloadSchema = z.object({
  operation: z.literal(ApiPatchOperation.UpdateResourceSection),
  sectionId: z.string(),
  section: SectionSchema.omit({
    parentSectionId: true, // deprecated
    layout: true,
  }),
});

export const createSubmissionBlockPayloadSchema = SubmissionBlockSchema.omit({
  // server-managed
  amended_at: true,
  amended_by: true,
  // omit deprecated fields
  systemHelperText: true,
  order: true,
  usedInTemplates: true,
  mrc_heading_type: true,
  copied_from_template_id: true,
  isVisible: true,
  isComplete: true,
}).extend({
  prevBlockOrder: z.number(),
  operation: z.string(),
});

export const createBlockPayloadSchema = LibraryBlockSchema.omit({
  // omit deprecated fields
  systemHelperText: true,
  order: true,
  usedInTemplates: true,
  mrc_heading_type: true,
  copied_from_template_id: true,
  isVisible: true,
  isComplete: true,
});

export const updateBlockPayloadSchema = createBlockPayloadSchema.partial().required({ name: true }); // For updates we don't want defaults to overwrite by accident

export const updateSubmissionBlockPayloadSchema = z
  .object({
    operation: z.literal(ApiPatchOperation.UpdateResourceBlock),
    blockId: z.string().uuid(),
    block: SubmissionBlockSchema.omit({
      // server-managed
      amended_at: true,
      amended_by: true,
      // omit deprecated fields
      systemHelperText: true,
      order: true,
      usedInTemplates: true,
      mrc_heading_type: true,
      copied_from_template_id: true,
      isVisible: true,
      isComplete: true,
    }),
  })
  .strict();

export const updateTemplateBlockPayloadSchema = z
  .object({
    operation: z.literal(ApiPatchOperation.UpdateResourceBlock),
    blockId: z.string().uuid(),
    block: TemplateBlockSchema.pick({
      section_id: true,
      visibility: true,
      canDeleteOnTemplate: true,
    }),
  })
  .strict();

export const addLibraryBlockToSubmissionPayloadSchema = z.object({
  operation: z.literal(ApiPatchOperation.AddNewBlockFromLibrary),
  blockLibraryId: z.string().uuid(),
  order: z.number(),
  sectionId: z.string().uuid(),
  canDeleteOnTemplate: z.boolean().default(true),
  canDelete: z.boolean().default(true),
  canEdit: z.boolean().default(true),
});

export const addLibraryBlockToTemplatePayloadSchema = TemplateBlockSchema.omit({
  section_id: true, // why is this replaced with the camelCase version?
}).extend({
  operation: z.literal(ApiPatchOperation.AddNewBlockFromLibrary),
  order: z.number(),
  sectionId: z.string().uuid(),
});

export const ADD_BLOCKS_FROM_LIBRARY_MAX = 40;

// Deliberately minimal — no per-block canDelete/canEdit/canDeleteOnTemplate.
// The handler derives those from each fetched library block instead of trusting
// client-sent values (see addLibraryBlockToSubmissionPayloadSchema's `.default(true)`
// quirk above, which this intentionally avoids repeating for the bulk case).
export const addLibraryBlocksToSubmissionPayloadSchema = z
  .object({
    operation: z.literal(ApiPatchOperation.AddNewBlocksFromLibrary),
    sectionId: z.string().uuid(),
    order: z.number(),
    blockLibraryIds: z.array(z.string().uuid()).min(1).max(ADD_BLOCKS_FROM_LIBRARY_MAX),
  })
  .strict()
  .refine((payload) => new Set(payload.blockLibraryIds).size === payload.blockLibraryIds.length, {
    message: 'blockLibraryIds must not contain duplicates',
    path: ['blockLibraryIds'],
  });

export const deleteContractBlockPayloadSchema = z.object({
  operation: z.literal(ApiPatchOperation.DeleteResourceBlock),
  blockId: z.string().uuid(),
});

export const BULK_DELETE_BLOCKS_MAX = 50;

export const bulkDeleteContractBlocksPayloadSchema = z
  .object({
    operation: z.literal(ApiPatchOperation.BulkDeleteResourceBlocks),
    sectionId: z.string().uuid(),
    blockIds: z.array(z.string().uuid()).min(1).max(BULK_DELETE_BLOCKS_MAX),
  })
  .strict()
  .refine((payload) => new Set(payload.blockIds).size === payload.blockIds.length, {
    message: 'blockIds must not contain duplicates',
    path: ['blockIds'],
  });

export const bulkDeleteSummaryBlocksPayloadSchema = z
  .object({
    operation: z.literal(ApiPatchOperation.BulkDeleteSummaryBlocks),
    sectionId: z.string().uuid(),
    blockIds: z.array(z.string().uuid()).min(1).max(BULK_DELETE_BLOCKS_MAX),
  })
  .strict()
  .refine((payload) => new Set(payload.blockIds).size === payload.blockIds.length, {
    message: 'blockIds must not contain duplicates',
    path: ['blockIds'],
  });

export const deleteContractSectionPayloadSchema = z.object({
  operation: z.literal(ApiPatchOperation.DeleteResourceSection),
  sectionId: z.string().uuid(),
});

export const reorderContractBlockPayloadSchema = z
  .object({
    operation: z.literal(ApiPatchOperation.ReorderResourceBlock),
    blockId: z.string(),
    reorder: z.object({
      destinationIndex: z.number(),
      destinationSectionId: z.string().optional(),
    }),
  })
  .strict();

export const reorderContractSectionPayloadSchema = z
  .object({
    operation: z.literal(ApiPatchOperation.ReorderResourceSection),
    sectionId: z.string(),
    reorder: z.object({
      destinationIndex: z.number(),
    }),
  })
  .strict();

export const createTagPayloadSchema = TagSchema;

export const createTeamPayloadSchema = TeamSchema;

export const updateTeamNamePayloadSchema = TeamSchema;

export const updateTeamStatusPayloadSchema = DbTeamSchema.pick({ status: true });

export const createUserPayloadSchema = dbUserSchema
  .pick({
    email: true,
    role: true,
    teams: true,
    firstName: true,
    lastName: true,
  })
  .extend({
    identityProvider: z.nativeEnum(IdentityProvider).default(IdentityProvider.Internal),
  })
  .strict();

export const updateUserPayloadSchema = createUserPayloadSchema
  .partial({
    email: true,
  })
  .extend({
    operation: z.literal(ApiPatchOperation.UserUpdate),
  })
  .strict();

export const userResendInvitePayloadSchema = z
  .object({
    operation: z.literal(ApiPatchOperation.UserResendInvite).default(ApiPatchOperation.UserResendInvite),
  })
  .strict();

export const userChangeStatePayloadSchema = z
  .object({
    operation: z.literal(ApiPatchOperation.UserChangeState).default(ApiPatchOperation.UserChangeState),
    enabled: z.boolean(),
  })
  .strict();

export const createAttachmentPayloadSchema = z.object({
  fileName: z.string(),
  name: z.string(),
  size: z.number(),
  type: z.enum(['pre', 'post']).optional(),
  section_id: z.string().uuid().optional(), // inline attachment as block
  order: z.number().optional(), // inline attachment as block
  block_type: z.enum(['block', 'summary_block']).optional(),
});

export const addNewCommentPayloadSchema = DbCommentSchema.pick({
  block_id: true,
  content: true,
  thread_id: true,
  anchor: true,
}).strict();

export const resolveCommentsThreadPayloadSchema = DbCommentSchema.pick({
  thread_id: true,
})
  .extend({
    operation: z.literal(ApiPatchOperation.ResolveCommentsThread),
  })
  .strict();

export const reopenCommentsThreadPayloadSchema = DbCommentSchema.pick({
  thread_id: true,
})
  .extend({
    operation: z.literal(ApiPatchOperation.ReopenCommentsThread),
  })
  .strict();

export const removeResourceAttachmentPayloadSchema = z.object({
  operation: z.literal(ApiPatchOperation.RemoveResourceAttachment),
  attachmentId: z.string().uuid(),
});

export const renameResourceAttachmentPayloadSchema = z.object({
  operation: z.literal(ApiPatchOperation.RenameResourceAttachment),
  attachmentId: z.string().uuid(),
  attachmentName: z.string(),
});

export const updateResourceAttachmentStatusPayloadSchema = z.object({
  operation: z.literal(ApiPatchOperation.UpdateResourceAttachmentStatus),
  attachmentId: z.string().uuid(),
  attachmentStatus: z.enum(['pending', 'ready']),
});

/**
 * Operation-only PATCH bodies. Their routes run no `validationSchema`, so these schemas type what the SPA sends; the
 * API does not enforce them.
 */
export const recreatePdfWithAttachmentPayloadSchema = z
  .object({ operation: z.literal(ApiPatchOperation.RecreatePdfWithAttachment) })
  .strict();

// The handler ignores `status`, but the SPA sends it as a readable marker of the transition: request logs whitelist
// it, and the e2e helpers match the finalise PATCH on it.
export const applyEndorsementPayloadSchema = z
  .object({
    operation: z.literal(ApiPatchOperation.ApplyEndorsement),
    status: z.literal('FINAL').optional(),
  })
  .strict();

const createEndorsementDateFieldSchema = (label: 'Effective date' | 'Expiry date') =>
  z
    .string({ required_error: `${label} is required` })
    .min(1, { message: `${label} is required` })
    .refine((value) => dayjs(value).isValid(), { message: 'Invalid date format' });

const createEndorsementAdditionalPremiumRefinement = (
  data: {
    premium_change: EndorsementPremiumChange;
    additional_premium?: { amount: number; currency: string } | null;
  },
  ctx: z.RefinementCtx,
) => {
  const requiresPremium =
    data.premium_change === EndorsementPremiumChange.ADDITION ||
    data.premium_change === EndorsementPremiumChange.RETURN;
  if (requiresPremium && (data.additional_premium?.amount === undefined || !data.additional_premium.currency?.trim())) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Additional premium is required',
      path: ['additional_premium'],
    });
  }
};

const createEndorsementBaseObjectSchema = z.object({
  additional_premium: z.object({ amount: z.number(), currency: z.string() }).nullable().optional(),
  endorsement_summary_template_id: z.number().optional(),
  internal_description: z.string().optional(),
  effective_date: createEndorsementDateFieldSchema('Effective date'),
  expiry_date: createEndorsementDateFieldSchema('Expiry date'),
  reference: z.string().min(1, { message: 'Reference is required' }),
  type: z.enum(ENDORSEMENT_TYPES, { required_error: 'Type is required' }),
  premium_change: z.nativeEnum(EndorsementPremiumChange),
  reason: z.enum(ENDORSEMENT_REASONS, { required_error: 'Reason is required' }),
});

// Used by the external M2M APIs (V2 Pro / V2 PPL) - no duplicate-an-existing-endorsement
// support there (SPA-only feature, see createEndorsementSpaPayloadSchema below).
export const createEndorsementPayloadSchema = createEndorsementBaseObjectSchema
  .strict()
  .superRefine(createEndorsementAdditionalPremiumRefinement);

// Used by the internal v2 SPA API only. Adds the duplicate-an-existing-endorsement path:
// when both are present, the new endorsement's content is cloned from this source instead
// of generated blank.
export const createEndorsementSpaPayloadSchema = createEndorsementBaseObjectSchema
  .extend({
    duplicateSourceContractId: z.string().optional(),
    duplicateSourceEndorsementId: z.string().optional(),
  })
  .strict()
  .superRefine((data, ctx) => {
    createEndorsementAdditionalPremiumRefinement(data, ctx);

    if (data.duplicateSourceContractId && !data.duplicateSourceEndorsementId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'duplicateSourceEndorsementId is required when duplicateSourceContractId is provided',
        path: ['duplicateSourceEndorsementId'],
      });
    }

    if (data.duplicateSourceEndorsementId && !data.duplicateSourceContractId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'duplicateSourceContractId is required when duplicateSourceEndorsementId is provided',
        path: ['duplicateSourceContractId'],
      });
    }

    if (
      data.endorsement_summary_template_id !== undefined &&
      (data.duplicateSourceContractId || data.duplicateSourceEndorsementId)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'endorsement_summary_template_id cannot be combined with the duplicate-source fields',
        path: ['endorsement_summary_template_id'],
      });
    }
  });

const createSubmissionProApiObjectSchema = z
  .object({
    status: z.enum([ResourceState.DRAFT, ResourceState.PRE_DRAFT]).default(ResourceState.DRAFT),
    template_id: z.string().uuid().optional(),
    submission_id: z.string().uuid().optional(),
    external_id: z.string().optional(),
    name: z.string(),
    created_by: z.string().optional(),
    teams: z.array(z.number()).max(1).optional(),
    data_items: z.record(z.string(), z.any()).optional(),
  })
  .passthrough();

const validateBaseForStatus = (data: z.infer<typeof createSubmissionProApiObjectSchema>, ctx: z.RefinementCtx) => {
  const { status, template_id, submission_id } = data;

  if (status === ResourceState.PRE_DRAFT && (template_id || submission_id)) {
    return ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'A PRE_DRAFT submission must not have a template_id or submission_id',
      path: ['status'],
    });
  }

  const hasExactlyOneBase = (template_id && !submission_id) || (!template_id && submission_id);

  if (status === ResourceState.DRAFT && !hasExactlyOneBase) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Exactly one of the following must be present: template_id, submission_id',
      path: ['template_id', 'submission_id'],
    });
  }
};

export const createSubmissionProApiPayloadSchema =
  createSubmissionProApiObjectSchema.superRefine(validateBaseForStatus);

export const submissionProApiTransitionPayloadSchema = z.object({
  status: DbSubmissionStatusSchema,
  operation: z.literal(ApiPatchOperation.UpdateStatus),
  shouldBypassDatapointValidation: z.boolean().optional(),
});

export const updateSubmissionProApiPayloadSchema = z
  .object({
    operation: z.literal(ApiPatchOperation.UpdateResourceData).default(ApiPatchOperation.UpdateResourceData),
    name: z.string().optional(),
    data_items: z.record(z.string(), z.any()).optional(),
  })
  .passthrough();

export const updateSubmissionExternalIdProApiPayloadSchema = z.object({
  operation: z.literal(ApiPatchOperation.UpdateResourceExternalId),
  external_id: z.string().nullable(),
});

export const updateEndorsementProApiPayloadSchema = z.object({
  operation: z.literal(ApiPatchOperation.UpdateResourceData).default(ApiPatchOperation.UpdateResourceData),
  data_items: z.record(z.string(), z.any()).optional(),
  additional_premium: z.object({ amount: z.number(), currency: z.string() }).nullable().optional(),
  effective_date: createEndorsementDateFieldSchema('Effective date').optional(),
  expiry_date: createEndorsementDateFieldSchema('Expiry date').optional(),
  internal_description: z.string().optional(),
  premium_change: z.nativeEnum(EndorsementPremiumChange).optional(),
  reason: z.enum(ENDORSEMENT_REASONS).optional(),
  reference: z.string().min(1, { message: 'Reference is required' }).optional(),
  type: z.enum(ENDORSEMENT_TYPES).optional(),
});

export const endorsementProApiTransitionPayloadSchema = DbEndorsementSchema.pick({
  status: true,
})
  .extend({
    operation: z.literal(ApiPatchOperation.UpdateStatus),
  })
  .strip();

export const addOrRemoveDataSectionsPayloadSchema = z.object({
  operation: z.literal(ApiPatchOperation.AddOrRemoveDataSections),
  data_items: z.record(z.string(), z.any()),
  sectionIndex: z.number(),
  action: z.enum(['ADD', 'REMOVE']),
});

export const restoreEndorsementSummaryTemplatePayloadSchema = z.object({
  operation: z.literal(ApiPatchOperation.RestoreEndorsementSummaryTemplate),
  endorsement_summary_template_id: z.number(),
});

export const batchCloneEndorsementPayloadSchema = z
  .object({
    operation: z.literal(ApiPatchOperation.BatchCloneEndorsement),
    targetSubmissionIds: z.array(z.string().uuid()).min(1),
  })
  .strict()
  .refine((payload) => new Set(payload.targetSubmissionIds).size === payload.targetSubmissionIds.length, {
    message: 'targetSubmissionIds must not contain duplicates',
    path: ['targetSubmissionIds'],
  });

const savedViewPayloadSchema = z
  .object({
    name: z.string().trim().min(1).max(SAVED_VIEW_NAME_MAX_LENGTH),
    filters: QueryFiltersSchema,
  })
  .strict();

export const createSavedViewPayloadSchema = savedViewPayloadSchema.extend({ filters: QueryFiltersSchema.min(1) });

// PUT = full-state upsert. No min(1) here: pre-guard records stored with empty filters must stay
// renameable (rename resends filters verbatim); the handler rejects emptying a non-empty view.
export const updateSavedViewPayloadSchema = savedViewPayloadSchema;

/**
 * Eval config payload schemas — Contract Review Bot eval period.
 * Grouped here so they can be removed together when the eval wraps up.
 */
export const createEvalConfigPayloadSchema = z
  .object({
    name: z.string().min(1),
    workflow_type: EvalWorkflowTypeSchema,
    model_id: EvalModelIdSchema.optional(),
    description: z.string().optional(),
    /**
     * Starting point for the draft (see `EVAL_CONFIG_STARTERS`). Absent or
     * `BLANK` creates an empty draft; `PBQA_STARTER` copies the bundled
     * starter packs in, and the starter's workflow type wins over
     * `workflow_type` — the packs only make sense under it.
     */
    copy_from: z
      .enum(EVAL_CONFIG_STARTERS.map(({ id }) => id) as [EvalConfigStarterId, ...EvalConfigStarterId[]])
      .optional(),
  })
  .strict();

export const updateEvalConfigMetaPayloadSchema = z
  .object({
    operation: z.literal(ApiPatchOperation.UpdateEvalConfigMeta),
  })
  .merge(
    EvalConfigRootSchema.pick({
      name: true,
      description: true,
      archived: true,
      model_id: true,
    }).partial(),
  )
  .strict();

export const updateEvalConfigDraftPayloadSchema = z
  .object({
    operation: z.literal(ApiPatchOperation.UpdateEvalConfigDraft),
  })
  .merge(EvalConfigDraftContentSchema)
  .strict();

export const publishEvalConfigDraftPayloadSchema = z
  .object({
    operation: z.literal(ApiPatchOperation.PublishEvalConfigDraft),
    change_note: z.string().optional(),
  })
  .strict();

/**
 * Promotes one config to champion — the only config whose runs brokers see.
 * No fields beyond the operation: the target is the config in the path, and
 * the handler clears the flag from whichever config held it before.
 */
export const setChampionEvalConfigPayloadSchema = z
  .object({
    operation: z.literal(ApiPatchOperation.SetChampionEvalConfig),
  })
  .strict();

/**
 * Eval-run payload schemas — Contract Review Bot evaluation feature.
 *
 * `StartEvalRun` is POSTed to the collection endpoint (no operation field),
 * whereas `CancelEvalRun`, `GradeEvalFinding`, `ResolveEvalFinding`,
 * `CommentEvalFinding` and `StartEvalResolutionCheck` are PATCH operations on
 * the item endpoint.
 */
export const startEvalRunPayloadSchema = z
  .object({
    eval_config_id: z.string().uuid().optional(),
    eval_config_version_number: z.number().int().positive().optional(),
  })
  .strict();

export const cancelEvalRunPayloadSchema = z
  .object({
    operation: z.literal(ApiPatchOperation.CancelEvalRun),
  })
  .strict();

export const gradeEvalFindingPayloadSchema = z
  .object({
    operation: z.literal(ApiPatchOperation.GradeEvalFinding),
    finding_id: z.string().uuid(),
    grade: EvalFindingGradeSchema.nullable(),
    grade_note: z.string().optional(),
  })
  .strict();

/** One operation covers resolve, dismiss and reopen (`resolution: null`). */
export const resolveEvalFindingPayloadSchema = z
  .object({
    operation: z.literal(ApiPatchOperation.ResolveEvalFinding),
    finding_id: z.string().uuid(),
    resolution: EvalFindingResolutionSchema.nullable(),
    resolution_note: z.string().optional(),
  })
  .strict();

/** Appends one broker comment to a finding's thread. */
export const commentEvalFindingPayloadSchema = z
  .object({
    operation: z.literal(ApiPatchOperation.CommentEvalFinding),
    finding_id: z.string().uuid(),
    text: z.string().min(1),
  })
  .strict();

/**
 * Appends one free-text note about the review as a whole — the channel for
 * "you missed X", which no per-finding comment can carry.
 */
export const sendEvalRunFeedbackPayloadSchema = z
  .object({
    operation: z.literal(ApiPatchOperation.SendEvalRunFeedback),
    text: z.string().min(1),
  })
  .strict();

/**
 * Starts an AI resolution check on a SUCCEEDED run. No fields: the scope is
 * always "every open canonical finding on this run", and the contract state
 * to assess is whatever is currently persisted.
 */
export const startEvalResolutionCheckPayloadSchema = z
  .object({
    operation: z.literal(ApiPatchOperation.StartEvalResolutionCheck),
  })
  .strict();

export type CreateTemplatePayload = z.infer<typeof createTemplatePayloadSchema>;
export type CreateSubmissionPayload = z.infer<typeof createSubmissionPayloadSchema>;
export type CreateRenewalSubmissionPayload = z.infer<typeof createRenewalSubmissionPayloadSchema>;
export type CreateRenewalSubmissionSpaPayload = z.infer<typeof createRenewalSubmissionSpaPayloadSchema>;
export type CreateRenewalSubmissionProApiPayload = z.infer<typeof createRenewalSubmissionProApiPayloadSchema>;
export type CreateRenewalSubmissionResponse = z.infer<typeof createRenewalSubmissionResponseSchema>;
export type UpdateContractPayload = z.infer<typeof updateSubmissionsPayloadSchema>;
export type UpdateTemplatePayload = z.infer<typeof updateTemplatePayloadSchema>;
export type UpdateResourceDataItemsPayload = z.infer<typeof updateResourceDataItemsPayloadSchema>;
export type CreateSubmissionBlockPayload = z.infer<typeof createSubmissionBlockPayloadSchema>;
export type UpdateSubmissionBlockPayload = z.infer<typeof updateSubmissionBlockPayloadSchema>;
export type UpdateTemplateBlockPayload = z.infer<typeof updateTemplateBlockPayloadSchema>;
export type AddLibraryBlockToSubmissionPayload = z.infer<typeof addLibraryBlockToSubmissionPayloadSchema>;
export type AddLibraryBlockToTemplatePayload = z.infer<typeof addLibraryBlockToTemplatePayloadSchema>;
export type AddLibraryBlocksToSubmissionPayload = z.infer<typeof addLibraryBlocksToSubmissionPayloadSchema>;
export type DeleteContractBlockPayload = z.infer<typeof deleteContractBlockPayloadSchema>;
export type BulkDeleteContractBlocksPayload = z.infer<typeof bulkDeleteContractBlocksPayloadSchema>;
export type BulkDeleteSummaryBlocksPayload = z.infer<typeof bulkDeleteSummaryBlocksPayloadSchema>;
export type DeleteContractSectionPayload = z.infer<typeof deleteContractSectionPayloadSchema>;
export type CreateContractSectionPayload = z.infer<typeof createContractSectionPayloadSchema>;
export type UpdateContractSectionPayload = z.infer<typeof updateContractSectionPayloadSchema>;
export type ReorderContractBlockPayload = z.infer<typeof reorderContractBlockPayloadSchema>;
export type ReorderContractSectionPayload = z.infer<typeof reorderContractSectionPayloadSchema>;
export type CreateBlockPayload = z.infer<typeof createBlockPayloadSchema>;
export type UpdateBlockPayload = z.infer<typeof updateBlockPayloadSchema>;
export type CreateTagPayload = z.infer<typeof createTagPayloadSchema>;
export type CreateTeamPayload = z.infer<typeof createTeamPayloadSchema>;
export type UpdateTeamNamePayload = z.infer<typeof updateTeamNamePayloadSchema>;
export type UpdateTeamStatusPayload = z.infer<typeof updateTeamStatusPayloadSchema>;
export type RootResourceName = z.infer<typeof rootResourceNameSchema>;
export type CreateUserPayload = z.infer<typeof createUserPayloadSchema>;
export type UpdateUserPayload = z.infer<typeof updateUserPayloadSchema>;
export type ChangeSubmissionBaseTemplatePayload = z.infer<typeof changeSubmissionBaseTemplatePayloadSchema>;
export type CreateEmptySubmissionPayload = z.infer<typeof createEmptySubmissionPayloadSchema>;
export type SubmissionSyncCheckPayload = z.infer<typeof submissionSyncCheckPayloadSchema>;
export type PreDraftToDraftPayload = z.infer<typeof preDraftToDraftPayloadSchema>;
export type SyncSubmissionDataPayload = z.infer<typeof syncSubmissionDataPayloadSchema>;
export type AddOrRemoveDataSectionsPayload = z.infer<typeof addOrRemoveDataSectionsPayloadSchema>;
export type BatchCloneEndorsementPayload = z.infer<typeof batchCloneEndorsementPayloadSchema>;
export type PplSubmissionReassignTeamPayload = z.infer<typeof pplSubmissionReassignTeamPayloadSchema>;
export type CreateAttachmentPayload = z.infer<typeof createAttachmentPayloadSchema>;
export type AddNewCommentPayload = z.infer<typeof addNewCommentPayloadSchema>;
export type ReopenCommentsThreadPayload = z.infer<typeof reopenCommentsThreadPayloadSchema>;
export type ResolveCommentsThreadPayload = z.infer<typeof resolveCommentsThreadPayloadSchema>;
export type RemoveResourceAttachmentPayload = z.infer<typeof removeResourceAttachmentPayloadSchema>;
export type RenameResourceAttachmentPayload = z.infer<typeof renameResourceAttachmentPayloadSchema>;
export type UpdateResourceAttachmentStatusPayload = z.infer<typeof updateResourceAttachmentStatusPayloadSchema>;
export type RecreatePdfWithAttachmentPayload = z.infer<typeof recreatePdfWithAttachmentPayloadSchema>;
export type ApplyEndorsementPayload = z.infer<typeof applyEndorsementPayloadSchema>;
export type RestoreEndorsementSummaryTemplatePayload = z.infer<typeof restoreEndorsementSummaryTemplatePayloadSchema>;
export type CreateEndorsementPayload = z.infer<typeof createEndorsementPayloadSchema>;
export type CreateEndorsementSpaPayload = z.infer<typeof createEndorsementSpaPayloadSchema>;
export type UpdateEndorsementProApiPayload = z.infer<typeof updateEndorsementProApiPayloadSchema>;
export type SubmissionProApiTransitionPayload = z.infer<typeof submissionProApiTransitionPayloadSchema>;
export type EndorsementProApiTransitionPayload = z.infer<typeof endorsementProApiTransitionPayloadSchema>;
export type UserResendInvitePayload = z.infer<typeof userResendInvitePayloadSchema>;
export type UserChangeStatePayload = z.infer<typeof userChangeStatePayloadSchema>;
export type CreateSavedViewPayload = z.infer<typeof createSavedViewPayloadSchema>;
export type UpdateSavedViewPayload = z.infer<typeof updateSavedViewPayloadSchema>;
export type CreateEvalConfigPayload = z.infer<typeof createEvalConfigPayloadSchema>;
export type UpdateEvalConfigMetaPayload = z.infer<typeof updateEvalConfigMetaPayloadSchema>;
export type UpdateEvalConfigDraftPayload = z.infer<typeof updateEvalConfigDraftPayloadSchema>;
export type PublishEvalConfigDraftPayload = z.infer<typeof publishEvalConfigDraftPayloadSchema>;
export type SetChampionEvalConfigPayload = z.infer<typeof setChampionEvalConfigPayloadSchema>;
export type StartEvalRunPayload = z.infer<typeof startEvalRunPayloadSchema>;
export type CancelEvalRunPayload = z.infer<typeof cancelEvalRunPayloadSchema>;
export type GradeEvalFindingPayload = z.infer<typeof gradeEvalFindingPayloadSchema>;
export type ResolveEvalFindingPayload = z.infer<typeof resolveEvalFindingPayloadSchema>;
export type CommentEvalFindingPayload = z.infer<typeof commentEvalFindingPayloadSchema>;
export type SendEvalRunFeedbackPayload = z.infer<typeof sendEvalRunFeedbackPayloadSchema>;
export type StartEvalResolutionCheckPayload = z.infer<typeof startEvalResolutionCheckPayloadSchema>;

// z.input variants — the request-building side, where defaulted fields stay optional.
export type AddLibraryBlockToSubmissionPayloadInput = z.input<typeof addLibraryBlockToSubmissionPayloadSchema>;
export type AddLibraryBlockToTemplatePayloadInput = z.input<typeof addLibraryBlockToTemplatePayloadSchema>;
export type CreateBlockPayloadInput = z.input<typeof createBlockPayloadSchema>;
export type CreateContractSectionPayloadInput = z.input<typeof createContractSectionPayloadSchema>;
export type CreateSubmissionBlockPayloadInput = z.input<typeof createSubmissionBlockPayloadSchema>;
// Its schema takes any operation string (see domain/CLAUDE.md), so DropApiOperation does not apply.
export type CreateResourceBlockPayload = Omit<CreateSubmissionBlockPayloadInput, 'operation' | 'prevBlockOrder'>;
export type CreateUserPayloadInput = z.input<typeof createUserPayloadSchema>;
export type UpdateBlockPayloadInput = z.input<typeof updateBlockPayloadSchema>;
export type UpdateContractPayloadInput = z.input<typeof updateSubmissionsPayloadSchema>;
export type UpdateEndorsementPayloadInput = z.input<typeof updateEndorsementPayloadSchema>;
export type UpdateResourceDataItemsPayloadInput = z.input<typeof updateResourceDataItemsPayloadSchema>;
export type UpdateSubmissionBlockPayloadInput = z.input<typeof updateSubmissionBlockPayloadSchema>;
export type UpdateTemplatePayloadInput = z.input<typeof updateTemplatePayloadSchema>;
export type UpdateUserPayloadInput = z.input<typeof updateUserPayloadSchema>;
